// Sdílené typy a pomocníci pro Sklad drobného materiálu (zipy, knoflíky…).

/** Položka katalogu drobného materiálu se stavem skladu. */
export type Material = {
  id: number;
  category: string;
  name: string;
  color: string;
  size: string;
  code: string;
  supplier: string;
  unit: 'ks' | 'm';
  stock_qty: number;
  min_qty: number;
  note: string;
  archived_at: number | null;
  /** Objednáno a ještě nepřijato (součet napříč otevřenými objednávkami). */
  on_order: number;
  /** Nejbližší očekávané dodání z otevřených objednávek. */
  next_expected_at: number | null;
  /** Rezervováno pro výrobu (zipy podle kusovníku): rozpracované zakázky mimo
   *  Došito/Hotovo + kusy zadané do výroby, které se ještě nestříhaly. */
  reserved: number;
  reserved_detail: { zakazky: number; plan: number };
  /** Položka vznikla ze zipu v Databázi zipů (barvu a velikost bere odtud). */
  zip_id: string | null;
  /** Náhled barvy „#rrggbb" (u zipů), jinak prázdné. */
  color_hex: string;
};

/** Kusy ve výrobě, u kterých neumíme určit zip (neúplný kusovník). */
export type ZipProblem = {
  product_code: string;
  variant_name: string | null;
  qty: number;
  problem: 'bez_barvy' | 'bez_delky' | 'neni_v_databazi';
  barva: string;
  delka: string;
};

/** Položka objednávky drobného materiálu (v `GET /api/sklad/orders`). */
export type MaterialOrderItem = {
  id: number;
  order_id: number;
  material_id: number;
  label: string;
  unit: string;
  quantity: number;
  received: number;
  received_at: number | null;
  category: string | null;
};

export type MaterialMovement = {
  id: number;
  type: 'receive' | 'unreceive' | 'issue' | 'adjust';
  delta: number;
  result_qty: number;
  production_ref: string | null;
  note: string;
  user_name: string;
  created_at: number;
};

/**
 * Stav zásoby počítaný z VOLNÉHO množství (sklad − rezervace pro výrobu):
 * `short` = na rozjetou výrobu nestačí ani sklad + to, co je na cestě,
 * `out` = volné nic, `low` = volné na minimu nebo pod ním, `ok` jinak.
 * `covered` = pod minimem, ale objednané množství to dorovná.
 */
export type StockLevel = 'short' | 'out' | 'low' | 'covered' | 'ok' | 'idle';

export function stockLevel(
  m: Pick<Material, 'stock_qty' | 'min_qty' | 'on_order'> & { reserved?: number },
): StockLevel {
  const reserved = m.reserved ?? 0;
  // Nikdo ji nepotřebuje (žádná rezervace ani minimum) a skladem nic není —
  // to není „došlo", jen se nepoužívá (např. zip ve velikosti, kterou nešijeme).
  if (reserved === 0 && m.min_qty <= 0 && m.stock_qty <= 0) return 'idle';
  if (m.stock_qty + m.on_order < reserved) return 'short';
  const free = m.stock_qty - reserved;
  const low = free <= 0 || (m.min_qty > 0 && free <= m.min_qty);
  if (!low) return 'ok';
  if (free + m.on_order > m.min_qty) return 'covered';
  return free <= 0 ? 'out' : 'low';
}

export const LEVEL_BADGE: Record<StockLevel, { label: string; cls: string }> = {
  short: { label: 'Chybí na výrobu', cls: 'text-white bg-danger' },
  out: { label: 'Došlo', cls: 'text-danger bg-danger-light' },
  low: { label: 'Doobjednat', cls: 'text-warning bg-warning-light' },
  covered: { label: 'Objednáno', cls: 'text-primary bg-primary-50' },
  ok: { label: 'OK', cls: 'text-success bg-success-light' },
  idle: { label: 'Nesleduje se', cls: 'text-text-muted bg-bg' },
};

/** „Zip spirálový · černá · 45 cm" */
export function materialLabel(m: Pick<Material, 'name' | 'color' | 'size'>): string {
  return [m.name, m.color, m.size].filter(Boolean).join(' · ');
}

export function formatAmount(n: number, unit: string): string {
  const v = Number.isInteger(n) ? n.toString() : n.toFixed(2).replace(/\.?0+$/, '');
  return `${v.replace('.', ',')} ${unit}`;
}

export const NO_CATEGORY = 'Bez druhu';

export const ZIP_PROBLEM_LABEL: Record<ZipProblem['problem'], string> = {
  bez_barvy: 'produkt nemá barvu zipu',
  bez_delky: 'kategorie nemá délku zipu pro tuhle velikost',
  neni_v_databazi: 'takový zip není v Databázi zipů',
};
